import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../App';
import AiLocked from '../components/AiLocked';
import { Loader2, Mic, MicOff, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';

const getRecognition = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const r = new SR();
  r.continuous = true;
  r.interimResults = true;
  r.lang = 'en-US';
  return r;
};

export default function VoiceInterviewPage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(true);
  const [jobRole, setJobRole] = useState('');
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [transcripts, setTranscripts] = useState([]);
  const [listening, setListening] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const baseRef = useRef('');

  useEffect(() => {
    setSupported(!!(window.SpeechRecognition || window.webkitSpeechRecognition));
    axios.get(`${API}/ai/usage`).then(r => { setUsage(r.data.voice_interview); setSecondsLeft((r.data.voice_max_minutes || 10) * 60); }).catch(() => {}).finally(() => setLoading(false));
    return () => { stopTimer(); try { recRef.current && recRef.current.stop(); } catch (e) {} };
  }, []);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { stopTimer(); stopListening(); submit(); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const start = async () => {
    if (!jobRole.trim()) { toast.error('Enter a target job role'); return; }
    setStarting(true); setFeedback(null);
    try {
      const r = await axios.post(`${API}/ai/voice-interview/start`, { job_role: jobRole });
      setSession(r.data);
      setTranscripts(r.data.questions.map(() => ''));
      setIdx(0);
      setSecondsLeft((r.data.max_minutes || 10) * 60);
      setUsage(u => ({ ...u, remaining: r.data.remaining }));
      startTimer();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start voice interview');
    } finally { setStarting(false); }
  };

  const startListening = () => {
    const rec = getRecognition();
    if (!rec) { setSupported(false); return; }
    recRef.current = rec;
    baseRef.current = transcripts[idx] || '';
    rec.onresult = (ev) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) interim += ev.results[i][0].transcript;
      setTranscripts(t => { const n = [...t]; n[idx] = (baseRef.current + ' ' + interim).trim(); return n; });
    };
    rec.onerror = (e) => { toast.error('Mic error: ' + (e.error || 'unknown') + '. You can also type your answer.'); setListening(false); };
    rec.onend = () => setListening(false);
    try { rec.start(); setListening(true); } catch (e) { toast.error('Could not access microphone.'); }
  };

  const stopListening = () => { try { recRef.current && recRef.current.stop(); } catch (e) {} setListening(false); };

  const submit = async () => {
    if (!session) return;
    stopListening(); stopTimer();
    setSubmitting(true);
    try {
      const payload = { session_id: session.session_id, answers: session.questions.map((q, i) => ({ question: q, answer: transcripts[i] })), duration_seconds: (session.max_minutes || 10) * 60 - secondsLeft };
      const r = await axios.post(`${API}/ai/voice-interview/submit`, payload);
      setFeedback(r.data.feedback);
      toast.success('Interview evaluated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not evaluate');
    } finally { setSubmitting(false); }
  };

  const mmss = `${String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:${String(secondsLeft % 60).padStart(2, '0')}`;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Mic className="w-6 h-6 text-[#CE1126]" />
          <h1 className="text-2xl font-bold">AI Voice Interview</h1>
        </div>

        {!usage?.has_access ? (
          <AiLocked title="AI Voice Interview" />
        ) : (
          <>
            {!supported && (
              <Card className="p-4 mb-4 bg-amber-50 border-amber-200 flex items-start gap-2" data-testid="voice-unsupported">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">Your browser doesn't support voice input. Please use Google Chrome, or type your answers below — they'll still be evaluated.</p>
              </Card>
            )}
            <p className="text-sm text-gray-500 mb-4" data-testid="voice-remaining">
              Voice sessions remaining: <strong>{usage.remaining}</strong> of {usage.limit} · Max {session?.max_minutes || 10} min each
            </p>

            {!session && (
              <Card className="p-6 space-y-4" data-testid="voice-setup">
                <div>
                  <label className="text-sm font-medium">Target Job Role</label>
                  <input value={jobRole} onChange={e => setJobRole(e.target.value)} placeholder="e.g. Marketing Manager"
                    className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1" data-testid="voice-role-input" />
                </div>
                <p className="text-xs text-gray-500">You'll be asked to allow microphone access. Speak your answers; a live transcript appears, and you can edit it before submitting.</p>
                <Button className="btn-uae-red" onClick={start} disabled={starting || usage.remaining <= 0} data-testid="voice-start-btn">
                  {starting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing…</> : 'Start Voice Interview'}
                </Button>
                {usage.remaining <= 0 && <p className="text-xs text-red-600">No voice sessions remaining in your package.</p>}
              </Card>
            )}

            {session && !feedback && (
              <Card className="p-6 space-y-4" data-testid="voice-active">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Question {idx + 1} of {session.questions.length}</p>
                  <span className={`text-sm font-mono font-bold ${secondsLeft < 60 ? 'text-red-600' : 'text-gray-700'}`} data-testid="voice-timer">{mmss}</span>
                </div>
                <p className="font-medium" data-testid="voice-question">{session.questions[idx]}</p>
                <textarea value={transcripts[idx]} onChange={e => { const t = [...transcripts]; t[idx] = e.target.value; setTranscripts(t); }}
                  rows={4} placeholder="Your spoken answer will appear here (editable)…" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="voice-transcript" />
                <div className="flex flex-wrap gap-2">
                  {!listening ? (
                    <Button className="btn-uae-green" onClick={startListening} disabled={!supported} data-testid="voice-record-btn"><Mic className="w-4 h-4 mr-1" />Record</Button>
                  ) : (
                    <Button variant="outline" onClick={stopListening} data-testid="voice-stop-btn"><MicOff className="w-4 h-4 mr-1" />Stop</Button>
                  )}
                  {idx < session.questions.length - 1 ? (
                    <Button variant="outline" onClick={() => { stopListening(); setIdx(idx + 1); }} data-testid="voice-next-btn">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
                  ) : (
                    <Button className="btn-uae-red" onClick={submit} disabled={submitting} data-testid="voice-submit-btn">
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Evaluating…</> : 'Finish & Get Feedback'}
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {feedback && (
              <div className="space-y-4" data-testid="voice-feedback">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">Overall Score</h3>
                    <span className="text-3xl font-bold text-[#CE1126]">{feedback.overall_score}</span>
                  </div>
                  <p className="text-sm text-gray-700">{feedback.summary}</p>
                </Card>
                {(feedback.per_question || []).map((p, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm font-medium flex-1">{p.question}</p>
                      <span className="text-sm font-bold text-gray-700">{p.score}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1"><CheckCircle2 className="w-3.5 h-3.5 inline text-[#009639] mr-1" />{p.feedback}</p>
                  </Card>
                ))}
                <Button variant="outline" onClick={() => { setSession(null); setFeedback(null); }} data-testid="voice-restart-btn">New Voice Interview</Button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
